import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { exchangeToken, getToken, onUnauthorized, setToken, verifyToken } from "@/api/client";
import { AuthScreen, EmailLink } from "./auth/AuthScreen";
import { Dialog } from "./ui";
import { LegalPage } from "@/pages/Legal";

/** The app behind sign-in. Signed in = a session cookie (e-mail, Google) or a stored access token.
 * Without either, the sign-in screens show; the e-mail links (/verify, /reset) open their own page.
 * Any 401 later signs the browser out again. */
export function TokenGate({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const path = window.location.pathname;
  const link = path === "/verify" ? "verify" : path === "/reset" ? "reset" : null;
  const [authed, setAuthed] = useState<boolean>(() => !!getToken() && !link);
  const [checking, setChecking] = useState<boolean>(() => !getToken() && !link);
  // A device still signed in with the setup token swaps it for a personal token before any data
  // request runs (the setup token is retired server-side, so in-flight requests must not use it).
  const [exchanging, setExchanging] = useState<boolean>(() => {
    const t = getToken();
    return !!t && !t.startsWith("amr_");
  });
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState<string | null>(null); // personal token from an exchange, shown once
  // the server couldn't say whether this browser is signed in (no answer, 5xx): not a sign-out (G3 H6)
  const [unreachable, setUnreachable] = useState<string | null>(null);
  const [ended, setEnded] = useState(false); // signed out by a 401 while using the app (G3 M8)

  useEffect(
    () =>
      onUnauthorized(() => {
        setToken(null);
        setAuthed((was) => {
          if (was) setEnded(true);
          return false;
        });
      }),
    [],
  );

  // a session cookie can't be read by script: ask the server once whether this browser is signed in
  useEffect(() => {
    if (!checking) return;
    const ctl = new AbortController(); // not AbortSignal.timeout: iOS 15 lacks it (G3 review)
    const timer = setTimeout(() => ctl.abort(), 15_000);
    fetch("/api/v1/account", { credentials: "same-origin", signal: ctl.signal })
      .then((r) => {
        if (r.ok) setAuthed(true);
        else if (r.status < 500 && r.status !== 408 && r.status !== 429)
          setAuthed(false); // an answer: not signed in
        else setUnreachable("Bason can't be reached right now. Try again in a moment.");
      })
      .catch(() => setUnreachable("No connection to Bason. Check your internet and try again."))
      .finally(() => {
        clearTimeout(timer);
        setChecking(false);
      });
  }, [checking]);

  const signedIn = () => {
    qc.clear();
    setAuthed(true);
  };

  useEffect(() => {
    const t = getToken();
    if (!exchanging || !t) return;
    void exchangeToken(t).then((nt) => {
      if (nt !== t) {
        setToken(nt);
        setIssued(nt);
      }
      setExchanging(false);
    });
  }, [exchanging]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const t = value.trim();
    if (!t) return;
    setBusy(true);
    setError(null);
    const ok = await verifyToken(t);
    if (!ok) {
      setBusy(false);
      setError("That token was not accepted.");
      return;
    }
    const nt = await exchangeToken(t);
    setToken(nt);
    if (nt !== t) setIssued(nt);
    setBusy(false);
    setValue("");
    qc.clear();
    setAuthed(true);
  }

  if (path.startsWith("/legal/")) return <LegalPage doc={path.slice(7)} />; // readable without an account
  if (path === "/help") return <LegalPage doc="guide" />;
  if (link) return <EmailLink kind={link} onSignedIn={signedIn} />;
  if (checking)
    return (
      <main className="gate">
        <p className="muted">Loading…</p>
      </main>
    );
  if (unreachable)
    return (
      <main className="gate stack" style={{ gap: 12 }}>
        <p role="alert">{unreachable}</p>
        <div>
          <button
            className="primary"
            onClick={() => {
              setUnreachable(null);
              setChecking(true);
            }}
          >
            Try again
          </button>
        </div>
      </main>
    );
  if (authed && exchanging)
    return (
      <main className="gate">
        <p className="muted">Signing in…</p>
      </main>
    );
  if (authed)
    return (
      <>
        {children}
        <Dialog open={!!issued} onClose={() => setIssued(null)} title="Your personal sign-in token">
          <div className="stack" style={{ gap: 10 }}>
            <p style={{ margin: 0 }}>
              The setup token is being retired (it stops working in 24 hours). Use this token to sign in on
              your other devices. Save it in your password manager; it is shown only now.
            </p>
            <div className="connector-url" data-testid="issued-token">
              {issued}
            </div>
            <div className="row">
              <button className="primary" onClick={() => issued && navigator.clipboard?.writeText(issued)}>
                Copy
              </button>
              <button onClick={() => setIssued(null)}>I saved it</button>
            </div>
          </div>
        </Dialog>
      </>
    );
  const tokenForm = (
    <form className="stack" onSubmit={submit} aria-label="Token sign-in">
      <p className="muted" style={{ margin: 0 }}>
        For scripts and the first setup: a token from Settings → API keys.
      </p>
      <label>
        Access token
        <input
          type="password"
          name="token"
          autoComplete="off" // a token, not the account password: password managers must not save it as one
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="amr_…"
          autoFocus
        />
      </label>
      {error && (
        <div className="badge bad" role="alert">
          {error}
        </div>
      )}
      <button className="primary" type="submit" disabled={busy}>
        {busy ? "Checking…" : "Continue"}
      </button>
    </form>
  );
  return (
    <>
      {ended && (
        <p
          className="badge warn"
          role="status"
          style={{ margin: "12px auto 0", display: "block", width: "fit-content" }}
        >
          Your session ended. Sign in again to continue.
        </p>
      )}
      <AuthScreen
        tokenForm={tokenForm}
        onSignedIn={() => {
          setEnded(false);
          signedIn();
        }}
      />
    </>
  );
}
